/**
 * 1.6.5 §2 — the service worker's part of "the app updates itself", without the build's virtual module (that is
 * lib/sw-register.ts), so it can be tested:
 *
 *  - `followWorker`: asks the browser for a new worker whenever the app comes to the front (visible again,
 *    pageshow) and hourly, and says when a new worker has taken control of this page;
 *  - `settleServiceWorker`: before a reload, will the reload be answered with the new app shell?
 *
 * Why the answer matters: the worker answers every navigation with the index.html in its own cache (workbox
 * precache, `navigateFallback`), whatever the address says — a query string does not get past it. A reload brings
 * the new shell only once the new worker is the active one ('ready'). Where there is no worker ('none' — iOS drops
 * registrations) or it does not take over in time ('refused'), the page has to go past it: lib/app-update.ts.
 */
const HOUR = 60 * 60 * 1000;
/** Visible and pageshow arrive together; one request for the pair. */
const FRONT_GAP_MS = 10 * 1000;

export type WorkerAnswer = 'ready' | 'none' | 'refused' | 'offline';

export interface WorkerFollow {
  /** The registration, once there is one. */
  attach(registration: ServiceWorkerRegistration | undefined): void;
  stop(): void;
}

export function followWorker(onNewVersion: () => void, target: Window = window): WorkerFollow {
  const container = target.navigator.serviceWorker as ServiceWorkerContainer | undefined;
  const doc = target.document;
  let registration: ServiceWorkerRegistration | undefined;
  let timer: number | undefined;
  let asked = -Infinity;
  const ask = () => {
    if (!registration || Date.now() - asked < FRONT_GAP_MS) return;
    asked = Date.now();
    void registration.update().catch(() => undefined); // offline: the next time will do
  };
  const seen = () => {
    if (doc.visibilityState === 'visible') ask();
  };
  // the first worker a page ever gets also "takes control" (clientsClaim): only a change from one to another is news
  let had = !!container?.controller;
  const changed = () => {
    if (had) onNewVersion();
    had = true;
  };
  doc.addEventListener('visibilitychange', seen);
  target.addEventListener('pageshow', ask);
  container?.addEventListener('controllerchange', changed);
  return {
    attach(reg) {
      registration = reg;
      if (reg && timer === undefined) {
        timer = target.setInterval(() => {
          asked = Date.now();
          void reg.update().catch(() => undefined);
        }, HOUR);
      }
    },
    stop() {
      if (timer !== undefined) target.clearInterval(timer);
      timer = undefined;
      registration = undefined;
      doc.removeEventListener('visibilitychange', seen);
      target.removeEventListener('pageshow', ask);
      container?.removeEventListener('controllerchange', changed);
    },
  };
}

/**
 * Looks for a new worker now, tells one that is installed to take over (the build's worker does so by itself; one
 * from an older build waits to be told), and waits — at most `maxMs` — until it is the active one.
 */
export async function settleServiceWorker(maxMs = 5000): Promise<WorkerAnswer> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker) return 'none';
  let wait: number | undefined;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return 'none';
    const activated = (w: ServiceWorker) =>
      new Promise<WorkerAnswer>((done) => {
        const look = () => {
          if (w.state === 'installed') w.postMessage({ type: 'SKIP_WAITING' });
          if (w.state === 'activated') done('ready');
          else if (w.state === 'redundant') done('refused');
        };
        w.addEventListener('statechange', look);
        look();
      });
    const work = (async (): Promise<WorkerAnswer> => {
      try {
        await reg.update();
      } catch {
        return 'offline';
      }
      const next = reg.installing ?? reg.waiting;
      if (next) return activated(next);
      return reg.active ? 'ready' : 'none';
    })();
    const late = new Promise<WorkerAnswer>((r) => {
      wait = window.setTimeout(() => r('refused'), maxMs);
    });
    return await Promise.race([work, late]);
  } catch {
    return 'none';
  } finally {
    if (wait !== undefined) window.clearTimeout(wait);
  }
}
